import type { Argument } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import Stack from '@mui/system/Stack';
import isNil from 'lodash/isNil';
import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';

import { LabwareSlotAllocation } from './LabwareSlotAllocation';
import { NoLabware } from './NoLabware';
import { TaskParameterField } from './TaskParameterField';
import { UnitsConsumedTable } from './UnitsConsumedTable';
import { isTimeField, paramToString } from './utils';
import { setWorkflowIsSaved } from '../../../../features/ui/uiSlice';
import { selectUnitsConsumedByTaskByLabware } from '../../../../features/workflow/selectors';
import {
  removeTaskParameter,
  updateUnitsConsumed,
} from '../../../../features/workflow/workflowSlice';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { useTask } from '../../../../hooks/tasks';
import type {
  InstrumentForLocation,
  TaskParameterValue,
  TaskProperties,
  TaskPropertiesForm,
  UpdateTaskParametersFn,
} from '../../../../types';
import {
  getTaskHasDeadEndNode,
  getVersionedDriverName,
  isNoLabwareEnabled,
} from '../../../../utils';
import {
  isSlotAllocatingInstrument,
  isTaskDoesNotSupportSlotAllocating,
} from '../../../../utils/instrumentCapabilities';
import { BaseTaskSettings } from '../BaseTaskSettings';
import { ConnectedDeckLayoutSection } from '../DeckLayoutSection';
import { FormSectionLabel } from '../FormSectionLabel';
import { TaskDependenciesTable } from '../TaskDependenciesTable';

export type TaskFieldsProps = {
  handlePendingChange: (update: Partial<TaskPropertiesForm>) => void;
  handleSubmitForm: (override?: Partial<TaskPropertiesForm>) => void;
  instrument: InstrumentForLocation | null;
  instruments: InstrumentForLocation[];
  selectedInstrumentDriverArgumentsForTask: {
    [parameterName: string]: Argument;
  } | null;
  showTimeEstimateField: boolean;
  taskProperties: TaskProperties;
  updateTaskParameters: UpdateTaskParametersFn;
  updateTaskProperties: (formUpdate: Partial<TaskPropertiesForm>) => void;
  values: TaskPropertiesForm;
};

const needsSlotAllocationUI = (
  driverName: string,
  taskType: string
): boolean => {
  return (
    !isTaskDoesNotSupportSlotAllocating(driverName, taskType) &&
    isSlotAllocatingInstrument(driverName)
  );
};

export const TaskFields = ({
  handlePendingChange,
  handleSubmitForm,
  instrument,
  instruments,
  selectedInstrumentDriverArgumentsForTask,
  showTimeEstimateField,
  taskProperties,
  updateTaskParameters,
  updateTaskProperties,
  values,
}: TaskFieldsProps) => {
  const dispatch = useAppDispatch();
  const id = values.id;
  const task = useTask(id);
  const [isRadiusVisible, setIsRadiusVisible] = useState(false);

  const unitsConsumedByLabware = useAppSelector(
    selectUnitsConsumedByTaskByLabware
  )[id];

  const shouldShowTaskParameters =
    selectedInstrumentDriverArgumentsForTask !== null &&
    Object.keys(selectedInstrumentDriverArgumentsForTask).length > 0;

  const reusableLabware = useMemo(
    () =>
      task.labware?.filter((labware) => {
        // TODO in follow-up ticket: need actual data to filter out labware
        // this is a placeholder, only tipracks will be shown bc they're multi by default
        return labware.singleOrMultiUse === 'multi';
      }),
    [task.labware]
  );

  const isUnitsConsumedTableVisible =
    reusableLabware !== undefined &&
    reusableLabware.length > 0 &&
    !getTaskHasDeadEndNode(task.taskType);

  const handleChangeUnitsConsumed = useCallback(
    ({
      labwareId,
      unitsConsumed,
    }: {
      labwareId: string;
      unitsConsumed: number | null;
    }) => {
      // If the units consumed is empty or greater than 0, update the state, otherwise it will return previously set valid value
      if (unitsConsumed === null || unitsConsumed > 0) {
        dispatch(updateUnitsConsumed({ taskId: id, labwareId, unitsConsumed }));
        dispatch(setWorkflowIsSaved(false));
      }
    },
    [dispatch, id]
  );
  const handleChangeSpeedUnit = useCallback(
    (speedUnit: 'g_force' | 'speed') => {
      setIsRadiusVisible(speedUnit === 'g_force');

      if (speedUnit === 'speed') {
        dispatch(
          removeTaskParameter({ id: taskProperties.id, fieldName: 'radius_mm' })
        );
      }
    },
    [dispatch, taskProperties.id]
  );

  const needsSlotAllocationFields =
    instrument && needsSlotAllocationUI(instrument.driverName, task.taskType);

  return (
    <>
      <BaseTaskSettings
        values={values}
        instruments={instruments}
        showTimeEstimateField={showTimeEstimateField}
        handlePendingChange={handlePendingChange}
        handleSubmitForm={handleSubmitForm}
      />
      {shouldShowTaskParameters && (
        <>
          <FormSectionLabel>Settings</FormSectionLabel>
          <Stack gap={1}>
            {sortBy(
              Object.entries(selectedInstrumentDriverArgumentsForTask),
              ([parameterName, { units }]) => {
                // HACK(IL, 2024-04-05): not sure what our complete rules for field order will be,
                // "time field(s) first" is just a starting point.
                return isTimeField(units)
                  ? `\u0000${parameterName}`
                  : parameterName;
              }
            ).map(([parameterName, parameter]) => {
              const formattedDefaultValue = paramToString(
                parameter.default as TaskParameterValue
              );
              const isDefaultApplied =
                isNil(taskProperties.taskParameters?.[parameterName]) &&
                formattedDefaultValue !== '';
              const savedParameterValue = paramToString(
                taskProperties.taskParameters?.[parameterName]
              );

              return (
                <TaskParameterField
                  taskId={id}
                  taskProperties={taskProperties}
                  key={parameterName}
                  parameter={parameter}
                  parameterName={parameterName}
                  parameterValue={savedParameterValue || formattedDefaultValue}
                  isDefaultApplied={isDefaultApplied}
                  versionedDriverName={
                    instrument &&
                    getVersionedDriverName({
                      name: instrument.driverName,
                      version: instrument.driverVersion,
                    })
                  }
                  updateTaskParameters={updateTaskParameters}
                  updateTaskProperties={updateTaskProperties}
                  handleChangeSpeedUnit={handleChangeSpeedUnit}
                  isRadiusVisible={isRadiusVisible}
                />
              );
            })}
          </Stack>
        </>
      )}
      {needsSlotAllocationFields && <LabwareSlotAllocation taskId={id} />}
      {isNoLabwareEnabled(task.taskType) && (
        <NoLabware
          isLabwareLessTask={task.isLabwareLessTask}
          taskId={task.id}
        />
      )}
      {isUnitsConsumedTableVisible && unitsConsumedByLabware && (
        <UnitsConsumedTable
          onChange={handleChangeUnitsConsumed}
          reusableLabware={reusableLabware}
          unitsConsumedByLabware={unitsConsumedByLabware}
        />
      )}
      <TaskDependenciesTable taskId={id} />
      <ConnectedDeckLayoutSection taskId={id} />
    </>
  );
};
